import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { PrimaryButton } from '@components/Button/variants';
import LazyImgixImage from '@components/LazyImgixImage';
import SectionBreakTitle, { SectionColor } from '@components/SectionBreakTitle';
import useScrollToHash from '@hooks/useScrollToHash';
import { getImgixSrc } from '@utils/images';

import { PAST_SPEAKERS, SPEAKER_FORM_URL } from './constant';

const SCROLL_END_THRESHOLD_PX = 4;

const PastSpeaker = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showBottomFade, setShowBottomFade] = useState(true);

  const updateBottomFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const distanceFromEnd = scrollHeight - scrollTop - clientHeight;
    const canScroll = scrollHeight > clientHeight + 1;
    setShowBottomFade(canScroll && distanceFromEnd > SCROLL_END_THRESHOLD_PX);
  }, []);
  const pastSpeakersRef = useScrollToHash({ hash: '#speakers-section', offset: 100 });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    updateBottomFade();
    const ro = new ResizeObserver(() => updateBottomFade());
    ro.observe(el);
    window.addEventListener('resize', updateBottomFade);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateBottomFade);
    };
  }, [updateBottomFade]);

  return (
    <div className="px-12">
      <div className="m-auto max-w-container-lg">
        <SectionBreakTitle
          className="mb-32"
          titleClassName="text-black"
          color={SectionColor.Gray}
          title="Past speakers"
        />
        <div
          id="speakers-section"
          className={clsx(
            'relative',
            showBottomFade &&
              "after:pointer-events-none after:absolute after:bottom-0 after:left-0 after:z-10 after:h-[120px] after:w-full after:bg-[linear-gradient(to_top,#fff_0%,rgba(255,255,255,0)_100%)] after:content-['']"
          )}
          ref={pastSpeakersRef}
        >
          <div
            ref={scrollRef}
            onScroll={updateBottomFade}
            className="grid max-h-[950px] grid-cols-2 gap-y-40 overflow-y-scroll [-ms-overflow-style:none] [scrollbar-width:none] sm:grid-cols-4 [&::-webkit-scrollbar]:hidden"
          >
            {PAST_SPEAKERS.map((speaker) => (
              <div key={speaker.name}>
                <LazyImgixImage
                  src={getImgixSrc({ path: speaker.image, isEnvDependent: false })}
                  alt={speaker.name}
                  className="h-full w-full object-cover"
                />
                <div className="p-16">
                  <h4 className="m-0 font-suisse-works text-headline-xxs text-black">
                    {speaker.name}
                  </h4>
                  <span className="mt-4 font-suisse-intl text-button-md text-grayscale-gray">
                    {speaker.title}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-112 flex flex-col items-center justify-center gap-24 border border-solid border-grayscale-gray px-72 py-64">
          <p className="m-0 p-0 text-center font-suisse-intl-mono text-feed-headline-sm text-grayscale-gray">
            To submit a speaker, select the relevant event page above. For events not listed, use
            our general speaker application here.
          </p>
          <PrimaryButton as="link" target="_blank" href={SPEAKER_FORM_URL}>
            Become a speaker
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default PastSpeaker;
